import intermittentFastingImage from '../Images/intermittent-fasting-featured.jpg';

const nutritionArticles = [
{
    id: 'nutrition-1',
    title: "How to Lose 20 Kilograms with Intermittent Fasting: A Complete Nutrition & Wellness Guide",
    category: "Nutrition",
    excerpt: "A science-backed approach to sustainable weight loss through intermittent fasting, covering timing, nutrition, and lifestyle strategies.",
    content: `
      <h2>Introduction</h2>
      <p>If you've tried countless diets but still struggle to lose weight, <strong>intermittent fasting (IF)</strong> might be the simple, science-backed approach you've been looking for.</p>

      <p>Rather than focusing on <em>what</em> you eat, intermittent fasting focuses on <em>when</em> you eat—helping your body switch to fat-burning mode naturally.</p>

      <p>In this guide, you'll learn:</p>
      <ul>
        <li>How intermittent fasting works</li>
        <li>What foods to eat (and when)</li>
        <li>Portion and timing tips</li>
        <li>How long it takes to lose 20 kilograms</li>
        <li>The science behind it (with references)</li>
      </ul>

      <h2>What Is Intermittent Fasting?</h2>
      <p>Intermittent fasting is a structured eating pattern that cycles between fasting and eating periods.</p>

      <p>During fasting, insulin levels drop and the body begins to <strong>burn stored fat for energy</strong>, improving metabolism and promoting sustainable weight loss.</p>

      <h3>Common IF Methods:</h3>
      <ul>
        <li><strong>16:8 Method:</strong> Fast for 16 hours, eat during an 8-hour window</li>
        <li><strong>18:6 Method:</strong> Fast for 18 hours, eat during a 6-hour window</li>
        <li><strong>5:2 Diet:</strong> Eat normally for 5 days, restrict calories to 500–600 on 2 days</li>
      </ul>

      <p>For beginners, the <strong>16:8 method</strong> is both effective and easy to maintain long term.</p>

      <h2>Example of a 16:8 Schedule</h2>
      <table class="article-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Activity</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>8:00 PM</td>
            <td>Stop eating</td>
            <td>Finish dinner</td>
          </tr>
          <tr>
            <td>8:00 PM – 12:00 PM (next day)</td>
            <td>Fasting period</td>
            <td>Water, black coffee, green tea</td>
          </tr>
          <tr>
            <td>12:00 PM – 8:00 PM</td>
            <td>Eating window</td>
            <td>2–3 balanced meals</td>
          </tr>
        </tbody>
      </table>

      <p><strong>Tip:</strong> Stay consistent with your fasting hours daily to see the best results.</p>

      <h2>What to Eat During the Eating Window</h2>

      <h3>1. Nutrient-Dense Foods</h3>
      <ul>
        <li><strong>Proteins:</strong> Chicken breast, fish, eggs, tofu, lentils</li>
        <li><strong>Healthy fats:</strong> Olive oil, avocado, nuts, seeds</li>
        <li><strong>Complex carbs:</strong> Oats, brown rice, quinoa, sweet potatoes</li>
        <li><strong>Fruits & veggies:</strong> Leafy greens, berries, citrus, cruciferous vegetables</li>
        <li><strong>Hydration:</strong> Drink 2–3 liters of water per day</li>
      </ul>

      <h3>2. Portion Control (Visual Guide)</h3>
      <table class="article-table">
        <thead>
          <tr>
            <th>Food Group</th>
            <th>Ideal Portion</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Protein</td>
            <td>Palm-sized</td>
            <td>Grilled chicken or fish (100–150 g)</td>
          </tr>
          <tr>
            <td>Carbs</td>
            <td>Fist-sized</td>
            <td>Brown rice or sweet potato</td>
          </tr>
          <tr>
            <td>Vegetables</td>
            <td>2 fists</td>
            <td>Salad or stir-fried veggies</td>
          </tr>
          <tr>
            <td>Healthy fats</td>
            <td>Thumb-sized</td>
            <td>Olive oil, nuts, or avocado</td>
          </tr>
        </tbody>
      </table>

      <h3>3. During Fasting – What's Allowed</h3>
      <p><strong>Allowed:</strong></p>
      <ul>
        <li>Water</li>
        <li>Black coffee (no sugar or milk)</li>
        <li>Green tea or herbal tea</li>
        <li>Zero-calorie electrolytes</li>
      </ul>

      <p><strong>Avoid:</strong> Sugary drinks, juice, milk, or alcohol—they break your fast.</p>

      <h2>Exercise & Lifestyle Balance</h2>
      <p>While intermittent fasting alone can help reduce fat, combining it with <strong>light exercise</strong> accelerates progress.</p>

      <h3>Recommended Routine:</h3>
      <ul>
        <li><strong>Cardio (3–4x/week):</strong> Brisk walking, cycling, swimming</li>
        <li><strong>Strength training (2–3x/week):</strong> Resistance bands or weights</li>
        <li><strong>Daily movement:</strong> Take stairs, walk after meals</li>
      </ul>

      <h3>Lifestyle Tips:</h3>
      <ul>
        <li>Sleep 7–8 hours per night</li>
        <li>Manage stress through meditation or deep breathing</li>
        <li>Avoid highly processed foods and sugary snacks</li>
      </ul>

      <h2>How Long to Lose 20 Kilograms</h2>
      <p>The rate of weight loss depends on your <strong>calorie deficit</strong> and activity level.</p>

      <table class="article-table">
        <thead>
          <tr>
            <th>Average Fat Loss Rate</th>
            <th>Estimated Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>0.5 – 1 kg per week</td>
            <td>20 – 40 weeks (≈5–9 months)</td>
          </tr>
        </tbody>
      </table>

      <p><strong>Note:</strong> Slow, steady weight loss preserves muscle and reduces the risk of rebound.</p>

      <h2>Sample Day Using the 16:8 Method</h2>
      <table class="article-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Meal</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>12:00 PM</td>
            <td>Lunch</td>
            <td>Grilled chicken (120 g), brown rice (1 cup), spinach salad with olive oil</td>
          </tr>
          <tr>
            <td>3:30 PM</td>
            <td>Snack</td>
            <td>Greek yogurt + handful of almonds</td>
          </tr>
          <tr>
            <td>7:30 PM</td>
            <td>Dinner</td>
            <td>Baked salmon (150 g), quinoa (½ cup), roasted vegetables</td>
          </tr>
          <tr>
            <td>8:00 PM</td>
            <td>Start fast</td>
            <td>Water or herbal tea</td>
          </tr>
        </tbody>
      </table>

      <h2>Important Precautions</h2>
      <p>You should <strong>avoid or consult your doctor</strong> before starting IF if you:</p>
      <ul>
        <li>Are pregnant or breastfeeding</li>
        <li>Have diabetes or blood sugar disorders</li>
        <li>Have a history of eating disorders</li>
      </ul>

      <p>Always listen to your body and seek professional advice before major dietary changes.</p>

      <h2>Conclusion</h2>
      <p>Intermittent fasting is more than a diet—it's a <strong>sustainable lifestyle</strong> that can help you burn fat, boost energy, and develop a healthier relationship with food.</p>

      <p>By pairing fasting with balanced nutrition, consistent movement, and good sleep, you can realistically lose <strong>20 kilograms in 5–9 months</strong>—and keep it off.</p>

      <h2>References</h2>
      <ul class="references-list">
        <li>Anton, S. D. et al. (2018). <em>Flipping the metabolic switch: Understanding and applying the health benefits of fasting.</em> <strong>Obesity</strong>, 26(2), 254–268. <a href="https://doi.org/10.1002/oby.22065" target="_blank" rel="noopener noreferrer">https://doi.org/10.1002/oby.22065</a></li>
        <li>Patterson, R. E., & Sears, D. D. (2017). <em>Metabolic effects of intermittent fasting.</em> <strong>Annual Review of Nutrition</strong>, 37, 371–393. <a href="https://doi.org/10.1146/annurev-nutr-071816-064634" target="_blank" rel="noopener noreferrer">https://doi.org/10.1146/annurev-nutr-071816-064634</a></li>
        <li>Varady, K. A. (2011). <em>Intermittent versus daily calorie restriction: Which diet regimen is more effective for weight loss?</em> <strong>Obesity Reviews</strong>, 12(7), e593–e601. <a href="https://doi.org/10.1111/j.1467-789X.2011.00873.x" target="_blank" rel="noopener noreferrer">https://doi.org/10.1111/j.1467-789X.2011.00873.x</a></li>
        <li>Harvie, M. N. & Howell, A. (2017). <em>Intermittent fasting: A simple route to weight management?</em> <strong>Obesity Research & Clinical Practice</strong>, 11(3), 266–275. <a href="https://doi.org/10.1016/j.orcp.2016.05.001" target="_blank" rel="noopener noreferrer">https://doi.org/10.1016/j.orcp.2016.05.001</a></li>
        <li>Tinsley, G. M., & La Bounty, P. M. (2015). <em>Effects of intermittent fasting on body composition and clinical health markers in humans.</em> <strong>Nutrition Reviews</strong>, 73(10), 661–674. <a href="https://doi.org/10.1093/nutrit/nuv041" target="_blank" rel="noopener noreferrer">https://doi.org/10.1093/nutrit/nuv041</a></li>
      </ul>
    `,
    featuredImage: intermittentFastingImage,
    date: "November 4, 2025",
    readTime: "12 min read",
    tags: ["intermittent-fasting", "weight-loss", "nutrition", "wellness", "healthy-eating", "Wellness", "Healthy Living", "Fasting Plan", "Diet Tips", "Fitness", "Lifestyle", "Metabolism"]
  }
];

export default nutritionArticles;
